/* Feature registry: collects every `features/<id>/index.ts` with import.meta.glob. Folders starting
   with "_" (examples) are skipped unless the URL has `?examples=1` (remembered for the session). */
import type { FeatureDescriptor } from './feature';
import { FEATURE_GROUPS } from './feature';

type Mod = { default?: FeatureDescriptor };

export function collectFeatures(modules: Record<string, Mod>, opts: { examples: boolean }): FeatureDescriptor[] {
  const out: FeatureDescriptor[] = [];
  const seen = new Set<string>();
  for (const [path, mod] of Object.entries(modules)) {
    const folder = /features\/([^/]+)\/index\.tsx?$/.exec(path)?.[1];
    if (!folder) continue;
    if (folder.startsWith('_') && !opts.examples) continue;
    const f = mod.default;
    if (!f || typeof f !== 'object' || !f.id || !f.route || !f.screen) continue;
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    out.push(f);
  }
  return out.sort((a, b) =>
    FEATURE_GROUPS.indexOf(a.group) - FEATURE_GROUPS.indexOf(b.group) || (a.order ?? 100) - (b.order ?? 100) || a.title.localeCompare(b.title));
}

export function examplesEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('examples');
    if (q === '1') sessionStorage.setItem('cx.examples', '1');
    if (q === '0') sessionStorage.removeItem('cx.examples');
    return q === '1' || sessionStorage.getItem('cx.examples') === '1';
  } catch {
    return new URLSearchParams(window.location.search).get('examples') === '1';
  }
}

const modules = import.meta.glob<Mod>('../features/*/index.ts', { eager: true });

let cached: FeatureDescriptor[] | null = null;
export function getFeatures(): FeatureDescriptor[] {
  return (cached ??= collectFeatures(modules, { examples: examplesEnabled() }));
}
